
// src/pages/videos/components/VideoCard.jsx
import React, { useMemo, useRef, useState, useEffect } from "react";
import Card from "react-bootstrap/Card";
import { Link } from "react-router-dom";
import { useAuth } from "../../../AuthUser";

function formatDuration(sec) {
  if (!sec || isNaN(sec)) return "";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}


/* -------------------------- progress helper --------------------------- */
function getProgressPercent(slug) {
  try {
    const raw = localStorage.getItem(`videoProgress:${slug}`);
    const p = raw ? JSON.parse(raw) : null;
    if (p?.duration > 0) {
      const pct = Math.round((p.seconds / p.duration) * 100);
      return pct >= 5 ? pct : 0;
    }
    return 0;
  } catch {
    return 0;
  }
}

/* --------------------- Preconnect / Prefetch helpers ------------------- */
function ensurePreconnect() {
  if (typeof document === "undefined") return;
  if (document.__mgd_preconnected_vimeo) return;
  document.__mgd_preconnected_vimeo = true;
  const add = (rel, href, crossOrigin) => {
    const l = document.createElement("link");
    l.rel = rel;
    l.href = href;
    if (crossOrigin) l.crossOrigin = crossOrigin;
    document.head.appendChild(l);
  };
  try {
    add("preconnect", "https://player.vimeo.com", "anonymous");
    add("preconnect", "https://i.vimeocdn.com", "anonymous");
    add("dns-prefetch", "//player.vimeo.com");
    add("dns-prefetch", "//i.vimeocdn.com");
  } catch {}
}
function prefetchOnce(url) {
  if (typeof document === "undefined" || !url) return;
  document.__mgd_prefetched = document.__mgd_prefetched || new Set();
  if (document.__mgd_prefetched.has(url)) return;
  document.__mgd_prefetched.add(url);
  const l = document.createElement("link");
  l.rel = "prefetch";
  l.href = url;
  l.as = "document";
  document.head.appendChild(l);
}

/* ------------------- global mobile autoplay controller -------------------
   Only activates a preview when scrolling becomes IDLE for 500ms.
   - No preview before user scrolls at least a little.
   - Uses IntersectionObserver to keep track of candidates.
   - On idle, picks the best candidate (highest ratio; tie-break closer to top).
--------------------------------------------------------------------------- */
function getMobileController() {
  if (!window.__MGD_MOBILE_PREVIEW__) {
    const ctrl = {
      currentId: null,
      visible: new Map(),     // Map<id, {top, ratio, ts}>
      listeners: new Set(),
      started: false,
      startScrollY: typeof window !== "undefined" ? window.scrollY : 0,
      idleTimer: null,
      SCROLL_IDLE_MS: 500,
      setActive(id) {
        if (this.currentId === id) return;
        this.currentId = id;
        this.listeners.forEach((fn) => fn(this.currentId));
      },
      onChange(fn) {
        this.listeners.add(fn);
        return () => this.listeners.delete(fn);
      },
      upsert(id, meta) {
        this.visible.set(id, meta);
      },
      remove(id) {
        this.visible.delete(id);
        if (this.currentId === id) {
          this.currentId = null;
          this.listeners.forEach((fn) => fn(this.currentId));
        }
      },
      pickBest() {
        let best = null;
        for (const [id, v] of this.visible.entries()) {
          const score = v.ratio * 1000 - Math.max(v.top, 0);
          if (!best || score > best.score) best = { id, score };
        }
        return best?.id || null;
      },
      choose() {
        if (!this.started) return this.setActive(null);
        const id = this.pickBest();
        this.setActive(id);
      },
      markStarted() {
        if (this.started) return;
        const delta = Math.abs((typeof window !== "undefined" ? window.scrollY : 0) - this.startScrollY);
        if (delta > 2) this.started = true;
      },
      scheduleIdle() {
        if (this.idleTimer) clearTimeout(this.idleTimer);
        this.idleTimer = setTimeout(() => {
          this.choose();
        }, this.SCROLL_IDLE_MS);
      },
    };

    // Listeners: when scrolling/resize, only schedule idle evaluation.
    const onScrollish = () => {
      ctrl.markStarted();
      ctrl.scheduleIdle();
    };
    window.addEventListener("scroll", onScrollish, { passive: true });
    window.addEventListener("resize", onScrollish);
    window.addEventListener("touchmove", onScrollish, { passive: true });
    window.addEventListener("wheel", onScrollish, { passive: true });

    window.__MGD_MOBILE_PREVIEW__ = ctrl;
  }
  return window.__MGD_MOBILE_PREVIEW__;
}

/* ---------------------- mobile autoplay helper hook --------------------- */
function useMobileTopAutoplay({ enabled, videoKey }) {
  const idRef = useRef(`vc_${Math.random().toString(36).slice(2)}`);
  const elRef = useRef(null);
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    const ctrl = getMobileController();
    const off = ctrl.onChange((currentId) => setIsActive(currentId === idRef.current));
    return off;
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !elRef.current) return;
    const ctrl = getMobileController();
    const target = elRef.current;

    const io = new IntersectionObserver(
        (entries) => {
          for (const e of entries) {
            const meta = {
              top: e.boundingClientRect.top,
              ratio: e.intersectionRatio,
              ts: performance.now(),
            };
            if (e.isIntersecting && e.intersectionRatio >= 0.6) {
              ctrl.upsert(idRef.current, meta);
            } else {
              ctrl.remove(idRef.current);
            }
          }
          // Do NOT choose immediately; wait for scroll idle.
          ctrl.scheduleIdle();
        },
        { root: null, rootMargin: "0px", threshold: [0, 0.25, 0.6, 0.85, 0.95, 1] }
    );

    io.observe(target);
    return () => {
      io.unobserve(target);
      const c = getMobileController();
      c.remove(idRef.current);
      c.scheduleIdle();
    };
  }, [enabled]);

  return { containerRef: elRef, isActive };
}

/* ----------------------------- HoverPreview ----------------------------- */
function HoverPreview({ vimeoId, poster, alt, disabled, forceActive = false, disableDesktopHover = false, onPlayed }) {
  const [active, setActive] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const timerRef = useRef(null);

  const HOVER_DELAY_MS = 700; // desktop hover only
  const LINGER_MS = 300;      // small linger on deactivate to avoid flicker

  const canHover = useMemo(() => {
    if (disabled) return false;
    if (forceActive) return true;
    if (typeof window === "undefined" || !window.matchMedia) return false;
    const isHoverDesktop = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
    if (isHoverDesktop && disableDesktopHover) return false;
    return isHoverDesktop;
  }, [disabled, forceActive, disableDesktopHover]);

  const src = useMemo(() => {
    if (!vimeoId) return null;
    const u = `https://player.vimeo.com/video/${encodeURIComponent(String(vimeoId))}?autoplay=1&muted=1&controls=0&background=1&playsinline=1&dnt=1&app_id=122963`;
    return u;
  }, [vimeoId]);

  const deactivate = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setActive(false);
      setLoaded(false);
    }, LINGER_MS);
  };

  useEffect(() => {
    if (forceActive && !disabled) {
      ensurePreconnect();
      if (src) prefetchOnce(src);
      if (timerRef.current) clearTimeout(timerRef.current);
      setLoaded(false);
      setActive(true);
    } else if (!forceActive) {
      deactivate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceActive, disabled, src]);

  useEffect(() => () => timerRef.current && clearTimeout(timerRef.current), []);

  if (!canHover || !vimeoId) {
    return (
        <div className="video-thumb-ratio">
          <img src={poster} className="video-thumb-img" alt={alt || "Video thumbnail"} loading="lazy" decoding="async" />
        </div>
    );
  }

  const handleEnter = () => {
    if (forceActive) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    setLoaded(false);
    timerRef.current = setTimeout(() => setActive(true), HOVER_DELAY_MS);
  };

  const handleLeave = () => {
    if (forceActive) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    deactivate();
  };

  return (
      <div className={`video-thumb-ratio ${active ? "is-playing" : ""}`} onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
        {(!active || !loaded) && (
            <img src={poster} className="video-thumb-img" alt={alt || "Video thumbnail"} loading="lazy" decoding="async" />
        )}
        {active && src && (
            <iframe
                className="video-thumb-iframe"
                src={src}
                title={alt || "Vimeo preview"}
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen={false}
                style={{ pointerEvents: "none" }}
                sandbox="allow-scripts allow-same-origin"
                onLoad={() => {
                  setLoaded(true);
                  onPlayed && onPlayed();
                }}
            />
        )}
        {active && !loaded && (
            <div className="video-thumb-spinner" aria-hidden>
              <div className="video-thumb-spinner-dot" />
            </div>
        )}
      </div>
  );
}

/* -------------------------- Modern lock overlay ------------------------- */
function ModernLockOverlay() {
  return (
      <div className="lock-modern">
        <div className="lock-badge">
          <i className="fa-solid fa-lock" />
        </div>
        <div className="lock-hint">Subscribe to watch</div>
      </div>
  );
}

/* -------------------------------- Card ---------------------------------- */
export default function VideoCard({ video, isLocked, disabledPreview = false }) {
  const auth = useAuth();
  const progress = getProgressPercent(video.slug);

  const vimeoId = useMemo(() => {
    if (video?.vimeoId) return String(video.vimeoId);
    const s = String(video?.slug || "");
    return /^\d+$/.test(s) ? s : null;
  }, [video]);

  const poster = video?.thumb || video?.thumbnail || "";
  const title = video?.title || "Untitled";
  const categoryTitle = video?.Category?.title || "";
  const to = `/videos/${video.slug}`;

  const previewDisabled = disabledPreview || !!isLocked || !auth?.user;

  const mobileEligible =
      !previewDisabled &&
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(hover: none) and (pointer: coarse)").matches &&
      (window.innerWidth || 0) <= 900;

  const { containerRef, isActive } = useMobileTopAutoplay({ enabled: mobileEligible, videoKey: vimeoId || video.slug });

  // If this card sits under an ancestor that opts out of desktop hover preview (e.g., sidebar)
  const [disableDesktopHover, setDisableDesktopHover] = useState(false);
  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const inNoHoverArea = !!el.closest(".no-desktop-hover-preview");
    setDisableDesktopHover(inNoHoverArea);
  }, [containerRef]);

  const handlePlayed = () => {
    try {
      sessionStorage.setItem(`mgd:warmed:${vimeoId || video.slug}`, "1");
    } catch {}
  };

  return (
      <Link to={to} className="text-decoration-none text-dark">
        <Card ref={containerRef} className="h-100 shadow-sm border-0 position-relative video-card">
          <div className="position-relative">
            <HoverPreview
                vimeoId={vimeoId}
                poster={poster}
                alt={title}
                disabled={previewDisabled}
                forceActive={mobileEligible && isActive}
                disableDesktopHover={disableDesktopHover}
                onPlayed={handlePlayed}
            />
            {/* Duration */}
            {video?.durationSeconds > 0 && (
                <div
                    className="position-absolute bottom-0 end-0 m-1 px-2 py-1 rounded text-white small"
                    style={{
                      background: "rgba(0,0,0,0.65)",
                      fontSize: "0.75rem",
                      lineHeight: 1,
                      pointerEvents: "none"
                    }}
                >
                  {formatDuration(video.durationSeconds)}
                </div>
            )}

            {/* Modern lock overlay */}
            {isLocked && <ModernLockOverlay />}

            {/* Progress */}
            {progress > 0 && (
                <div className="position-absolute bottom-0 start-0 w-100 bg-light" style={{ height: 4 }}>
                  <div className="progress-line" style={{ width: `${progress}%`, height: 4 }} />
                </div>
            )}
          </div>

          <Card.Body className="d-flex flex-column">
            <Card.Title className="fs-6 mb-1">{title}</Card.Title>
            <div className="text-muted small mb-1">{categoryTitle}</div>
          </Card.Body>
        </Card>
      </Link>
  );
}
